<template>
  <div class="lighting-advisor-question-fields">
    <template v-if="usesColumns">
      <ColumnsBlock
        v-for="(row, rowIndex) in questionRows"
        :key="`row-${rowIndex}`"
        class="lighting-advisor-question-fields__row"
      >
        <template v-for="cell in row" :key="cell.slot" #[cell.slot]>
          <LightingAdvisorQuestionField
            v-if="cell.question"
            :question="cell.question"
            :answers="answers"
            :layout="layout"
            :get-tooltip-keys="getTooltipKeys"
            :get-warning-notes="getWarningNotes"
            :nested-question="nestedQuestionFor(cell.question)"
            :option-images="optionImages"
            @update:answer="forwardAnswer"
            @open-tooltip="openTooltip"
            @update:focused-field="forwardFocusedField"
          />
        </template>
      </ColumnsBlock>
    </template>

    <template v-else>
      <LightingAdvisorQuestionField
        v-for="question in visibleQuestions"
        :key="question.id"
        class="lighting-advisor-question-fields__stacked-item"
        :question="question"
        :answers="answers"
        :layout="layout"
        :get-tooltip-keys="getTooltipKeys"
        :get-warning-notes="getWarningNotes"
        :option-images="optionImages"
        @update:answer="forwardAnswer"
        @open-tooltip="openTooltip"
        @update:focused-field="forwardFocusedField"
      />
    </template>

    <ModalModule
      v-model="tooltipModalOpen"
      :title="tooltipModalTitle"
      :close-icon-text="modalCloseIconText"
      :adaptive-width="true"
      :esc-to-close="true"
      :click-to-close="true"
      @abort="tooltipModalOpen = false"
    >
      <!--
        `ColumnsBlock` forces nested text-media modules to full width, so it is
        only used when the illustration actually needs a second column.
      -->
      <ColumnsBlock v-if="tooltipModalImage">
        <template #col1>
          <Media
            :media="tooltipModalImage"
            :cols="[12, 12, 12]"
            :lazyload="true"
          />
        </template>
        <template #col2>
          <DictionaryRichText
            v-for="key in tooltipModalKeys"
            :key="key"
            :dictionary-key="key"
            class="lighting-advisor-question-fields__tooltip-content"
          />
        </template>
      </ColumnsBlock>
      <template v-else>
        <DictionaryRichText
          v-for="key in tooltipModalKeys"
          :key="key"
          :dictionary-key="key"
          class="lighting-advisor-question-fields__tooltip-content"
        />
      </template>
    </ModalModule>
  </div>
</template>

<script setup lang="ts">
import {
  ColumnsBlock,
  Media,
  ModalModule
} from '@hypercodestudio/basler-components';
import type {
  MediaItemInterface,
  MediaImageInterface
} from '@hypercodestudio/basler-components';

import LightingAdvisorQuestionField from '~/components/global/lib/LightingAdvisor/LightingAdvisorQuestionField.vue';
import type {
  LightingAdvisorQuestion,
  LightingAdvisorSetupField,
  LightingAnswers
} from '~/types/lightingAdvisor';
import {
  DIMENSION_QUESTION_ID,
  DISTANCE_QUESTION_ID,
  type LightingAdvisorQuestionLayout,
  NESTED_QUESTION_ID,
  buildQuestionChoiceOptions,
  isCheckboxQuestion,
  isRadioQuestion,
  isTwoColumnLayout
} from '~/utils/lightingAdvisor/questionFieldKinds';

/** Question the nested peak wavelength field is attached to in the modal. */
const NESTED_QUESTION_PARENT_ID = 'a2s3q2';

interface Props {
  questions: ReadonlyArray<LightingAdvisorQuestion>;
  answers: LightingAnswers;
  isQuestionVisible: (questionId: string) => boolean;
  getTooltipKeys: (question: LightingAdvisorQuestion) => string[];
  getWarningNotes: (
    question: LightingAdvisorQuestion
  ) => Array<{ text: string }>;
  optionImages?: Readonly<Partial<Record<string, MediaImageInterface>>>;
  /** Question info illustrations for the explanation modal, keyed by question id. */
  overlayImages?: Readonly<Partial<Record<string, MediaItemInterface>>>;
  layout?: LightingAdvisorQuestionLayout;
}

const props = withDefaults(defineProps<Props>(), {
  layout: 'plp-list',
  optionImages: () => ({}),
  overlayImages: () => ({})
});

const emit = defineEmits<{
  'update:answer': [questionId: string, value: string | null];
  'update:focused-field': [field: LightingAdvisorSetupField | null];
  'tooltip-opened': [question: LightingAdvisorQuestion];
}>();

const { $textDictionary } = useNuxtApp();

const tooltipModalOpen = ref(false);
const tooltipModalTitle = ref('');
const tooltipModalKeys = ref<string[]>([]);
const tooltipModalImage = ref<MediaItemInterface>();

const modalCloseIconText = computed(
  () =>
    $textDictionary.value['productFinder.modal.common.closeIconText'] ?? 'close'
);

const usesModalLayout = computed(() => props.layout === 'modal-two-column');
const usesColumns = computed(() => isTwoColumnLayout(props.layout));

function choiceOptions(question: LightingAdvisorQuestion) {
  return buildQuestionChoiceOptions(
    question,
    props.answers,
    props.optionImages
  );
}

function hasRenderableControl(question: LightingAdvisorQuestion): boolean {
  if (
    question.id === DIMENSION_QUESTION_ID ||
    question.id === DISTANCE_QUESTION_ID ||
    isCheckboxQuestion(question.id)
  ) {
    return true;
  }
  return choiceOptions(question).length > 0;
}

const visibleQuestions = computed(() =>
  props.questions.filter((question) => {
    // Column layouts render the peak wavelength question inside its parent
    // field so it stays attached to the light feature it belongs to.
    if (usesColumns.value && question.id === NESTED_QUESTION_ID) {
      return false;
    }
    return (
      props.isQuestionVisible(question.id) && hasRenderableControl(question)
    );
  })
);

const nestedQuestion = computed(() => {
  if (!usesColumns.value) {
    return null;
  }
  const question = props.questions.find(
    (item) => item.id === NESTED_QUESTION_ID
  );
  if (
    !question ||
    !props.isQuestionVisible(question.id) ||
    !hasRenderableControl(question)
  ) {
    return null;
  }
  return question;
});

function nestedQuestionFor(
  question: LightingAdvisorQuestion
): LightingAdvisorQuestion | null {
  return question.id === NESTED_QUESTION_PARENT_ID
    ? nestedQuestion.value
    : null;
}

/** Questions whose control needs the full row instead of a single column. */
function spansBothColumns(question: LightingAdvisorQuestion): boolean {
  if (isCheckboxQuestion(question.id)) {
    return true;
  }
  // The modal has less horizontal room, so the paired dimension inputs there
  // need the full row; the wizard keeps them half-width like the legacy tool.
  if (usesModalLayout.value && question.id === DIMENSION_QUESTION_ID) {
    return true;
  }
  return choiceOptions(question).length > 0 && !isRadioQuestion(question.id);
}

/**
 * One `ColumnsBlock` slot. `ColumnsBlock` derives its column count from the
 * number of slots, so an empty cell keeps a lone question at half width.
 */
interface QuestionCell {
  slot: string;
  question: LightingAdvisorQuestion | null;
}

function toRow(questions: Array<LightingAdvisorQuestion | null>) {
  return questions.map((question, index) => ({
    slot: `col${index + 1}`,
    question
  }));
}

/**
 * Groups the questions into rows of two, giving full-row questions a row of
 * their own and padding a trailing single question with an empty column.
 */
const questionRows = computed(() => {
  const rows: QuestionCell[][] = [];
  let pair: LightingAdvisorQuestion[] = [];

  const flushPair = () => {
    if (pair.length === 0) {
      return;
    }
    rows.push(toRow(pair.length === 1 ? [pair[0], null] : pair));
    pair = [];
  };

  for (const question of visibleQuestions.value) {
    if (spansBothColumns(question)) {
      flushPair();
      rows.push(toRow([question]));
      continue;
    }
    pair.push(question);
    if (pair.length === 2) {
      flushPair();
    }
  }
  flushPair();

  return rows;
});

function forwardAnswer(questionId: string, value: string | null) {
  emit('update:answer', questionId, value);
}

function forwardFocusedField(field: LightingAdvisorSetupField | null) {
  emit('update:focused-field', field);
}

function openTooltip(question: LightingAdvisorQuestion) {
  const keys = props.getTooltipKeys(question);
  if (keys.length === 0) {
    return;
  }
  tooltipModalTitle.value = question.label || question.content;
  tooltipModalKeys.value = keys;
  tooltipModalImage.value = props.overlayImages[question.id];
  tooltipModalOpen.value = true;
  emit('tooltip-opened', question);
}
</script>

<style scoped>
.lighting-advisor-question-fields__row
  + .lighting-advisor-question-fields__row {
  margin-top: 1.5rem;
}

.lighting-advisor-question-fields__stacked-item
  + .lighting-advisor-question-fields__stacked-item {
  margin-top: 1.25rem;
}
</style>
